import { useCompanyBranchList } from "@/api/services/main/company";
import { TGetCompanyBranchListDto } from "@/api/services/main/company/dtos/get-company-branch-list.dto";
import { Button } from "@/components/elements/Button";
import { Pagination } from "@/components/elements/Pagination";
import { Table } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { useSelectionTable } from "@/hooks/useSelectionTable";
import { AppRoutes } from "@/routes/config";
import { convertNumberToZipCode } from "@/utils/input";
import { getFullAddress, truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";

type TCompanyBranchListProps = {
  onSelectionChange?: (selectedIds: string[]) => void;
};

export const CompanyBranchList: FC<TCompanyBranchListProps> = ({ onSelectionChange }) => {
  const { data, isLoading } = useCompanyBranchList();

  const renderCompanies = useMemo(() => {
    if (!data) return null;
    data.data.sort((a, b) => (b.isHeadquarters ? 1 : 0) - (a.isHeadquarters ? 1 : 0));
    return data;
  }, [data]);

  const columns = useMemo<MRT_ColumnDef<TGetCompanyBranchListDto>[]>(
    () => [
      {
        header: "事業所名",
        Cell: ({ row }) =>
          row.original.isHeadquarters
            ? `${truncateWithEllipsis(row.original.branchName, 25)} （本店）` || EMPTY_STR.TEXT
            : truncateWithEllipsis(row.original.branchName, 25) || EMPTY_STR.TEXT,
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "郵便番号",
        Cell: ({ row }) => (row.original.zipCode ? convertNumberToZipCode(row.original.zipCode) : EMPTY_STR.TEXT),
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "所在地",
        Cell: ({ row }) => {
          const { province, address, municipality } = row.original;
          return (
            <Typography variant="body14">
              {truncateWithEllipsis(getFullAddress({ province: province, municipality: municipality, address: address }), 30)}
            </Typography>
          );
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "電話番号",
        Cell: ({ row }) => row.original.telNumber || EMPTY_STR.TEXT,
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "編集",
        Cell: ({ row }) => {
          const { id } = row.original;
          return (
            <Button variant="outline" size="sm" component={Link} to={getDynamicRoute(AppRoutes.companyBranchEdit, { id })}>
              編集
            </Button>
          );
        },
        size: 80,
      },
    ],
    [data],
  );

  const { table, rowSelectionKeys } = useSelectionTable({ columns, rows: renderCompanies?.data ?? [], primaryKey: "id", defaultSelectionKeys: [] });

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(rowSelectionKeys);
    }
  }, [rowSelectionKeys, onSelectionChange]);

  return (
    <Stack gap={2}>
      <Pagination totalPage={data?.pagination.totalPage} />
      <Table table={table} isLoading={isLoading} />
    </Stack>
  );
};
